import { Component, OnInit, AfterViewInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Router, NavigationStart, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Button } from 'ustore-ui';

import { CustomInputComponent } from '../../shared/components/custom-input/custom-input.component';
import { TabsComponent, TabItem } from '../../shared/components/tabs/tabs.component';
import { PartnerService } from '../../core/services/partner.service';
import { PartnerStatusResponse } from '../../core/models/partner.model';
import { ToastService } from '../../shared/services/toast.service';
import { CustomValidators } from '../../shared/validators';
import { CnpjPipe } from '../../shared/pipes/cnpj.pipe';
import { I18nService } from '../../core/services/i18n.service';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { HighlightCardComponent } from '../../shared/components/highlight-card/highlight-card.component';
import {
  HorizontalStepperComponent,
  HorizontalStepperStep,
} from '../../shared/components/horizontal-stepper/horizontal-stepper.component';
import { ImageFallbackPipe } from '../../shared/pipes/image-fallback.pipe';
import { BackButtonComponent } from '../../shared/components/back-button/back-button.component';

@Component({
  selector: 'umkt-cadastro-parceiros',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    Button,
    CustomInputComponent,
    TabsComponent,
    CnpjPipe,
    I18nPipe,
    HighlightCardComponent,
    HorizontalStepperComponent,
    ImageFallbackPipe,
    BackButtonComponent,
  ],
  templateUrl: './cadastro-parceiros.component.html',
  styleUrls: ['./cadastro-parceiros.component.scss'],
})
export class CadastroParceirosComponent implements OnInit, AfterViewInit, OnDestroy {
  statusForm: FormGroup;
  tokenForm: FormGroup;
  isConsultingStatus = false;
  isVerifyingToken = false;
  statusStep: 'email' | 'token' | 'lista' = 'email';
  partnerRequests: PartnerStatusResponse[] = [];

  tabs: TabItem[] = [];
  activeTabId: string = 'enviar-solicitacao';
  private langSubscription?: Subscription;

  private scrollHandler?: () => void;
  private visibilityChangeHandler?: () => void;
  private destroy$ = new Subject<void>();
  private savedScrollPosition: number = 0;
  private shouldRestoreScroll: boolean = false;
  private scrollRestoreInterval?: any;

  headerStats: { value: string; label: string }[] = [];

  headerBenefits: { text: string }[] = [];

  partnerJourneySteps: HorizontalStepperStep[] = [];
  private formBuilder = inject(FormBuilder);
  private partnerService = inject(PartnerService);
  private toastService = inject(ToastService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private i18n = inject(I18nService);

  constructor() {
    this.statusForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email, CustomValidators.emailNoSpaces, Validators.maxLength(255)]]
    });

    this.tokenForm = this.formBuilder.group({
      token: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.langSubscription = this.i18n.getCurrentLanguage().subscribe(() => {
      this.loadTabs();
      this.loadHeaderStats();
      this.loadHeaderBenefits();
      this.loadPartnerJourneySteps();
    });

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const tab = params['tab'];

        if (tab === 'acompanhar-status') {
          this.activeTabId = 'acompanhar-status';
          this.statusStep = 'email';
        } else {
          this.activeTabId = 'enviar-solicitacao';
        }
      });

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        if (this.shouldRestoreScroll) {
          const currentUrl = this.router.url.split('?')[0];
          const newUrl = event.urlAfterRedirects.split('?')[0];
          const isQueryParamsOnly = currentUrl === newUrl;
          
          if (isQueryParamsOnly) {
            if (this.scrollRestoreInterval) {
              clearInterval(this.scrollRestoreInterval);
            }
            
            window.scrollTo({ top: this.savedScrollPosition, behavior: 'auto' });
            
            let attempts = 0;
            const maxAttempts = 30;
            
            this.scrollRestoreInterval = setInterval(() => {
              if (!this.shouldRestoreScroll) {
                clearInterval(this.scrollRestoreInterval);
                this.scrollRestoreInterval = undefined;
                return;
              }
              const currentScroll = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
              
              if (Math.abs(currentScroll - this.savedScrollPosition) > 5) {
                window.scrollTo({ top: this.savedScrollPosition, behavior: 'auto' });
                attempts++;
                
                if (attempts >= maxAttempts || Math.abs(window.scrollY - this.savedScrollPosition) <= 5) {
                  clearInterval(this.scrollRestoreInterval);
                  this.scrollRestoreInterval = undefined;
                  this.shouldRestoreScroll = false;
                }
              } else {
                attempts++;
                if (attempts >= 5) {
                  clearInterval(this.scrollRestoreInterval);
                  this.scrollRestoreInterval = undefined;
                  this.shouldRestoreScroll = false;
                }
              }
            }, 100);
          } else {
            this.shouldRestoreScroll = false;
          }
        }
      });

    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // ... navigation cleanup if needed
      });
  }

  private loadTabs() {
    this.tabs = [
      {
        id: 'enviar-solicitacao',
        label: this.i18n.instant('pages.partnerRegistration.tabs.sendRequest')
      },
      {
        id: 'acompanhar-status',
        label: this.i18n.instant('pages.partnerRegistration.tabs.trackStatus')
      }
    ];
  }

  private loadHeaderStats(): void {
    const base = 'pages.partnerRegistration.stats';
    this.headerStats = [
      { value: this.i18n.instant(`${base}.clients.value`), label: this.i18n.instant(`${base}.clients.label`) },
      { value: this.i18n.instant(`${base}.pmes.value`), label: this.i18n.instant(`${base}.pmes.label`) },
      { value: this.i18n.instant(`${base}.sla.value`), label: this.i18n.instant(`${base}.sla.label`) },
      { value: this.i18n.instant(`${base}.timeToMarket.value`), label: this.i18n.instant(`${base}.timeToMarket.label`) },
    ];
  }

  private loadHeaderBenefits(): void {
    const base = 'pages.partnerRegistration.benefits';
    this.headerBenefits = [
      { text: this.i18n.instant(`${base}.item1`) },
      { text: this.i18n.instant(`${base}.item2`) },
      { text: this.i18n.instant(`${base}.item3`) },
    ];
  }

  private loadPartnerJourneySteps(): void {
    const base = 'pages.partnerRegistration.howItWorks.steps';
    this.partnerJourneySteps = [
      {
        title: this.i18n.instant(`${base}.step1.title`),
        description: this.i18n.instant(`${base}.step1.description`),
      },
      {
        title: this.i18n.instant(`${base}.step2.title`),
        description: this.i18n.instant(`${base}.step2.description`),
      },
      {
        title: this.i18n.instant(`${base}.step3.title`),
        description: this.i18n.instant(`${base}.step3.description`),
      },
      {
        title: this.i18n.instant(`${base}.step4.title`),
        description: this.i18n.instant(`${base}.step4.description`),
      },
    ];
  }

  ngAfterViewInit(): void {
    this.addScrollListener();
  }

  ngOnDestroy(): void {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }

    if (this.scrollRestoreInterval) {
      clearInterval(this.scrollRestoreInterval);
    }

    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }

    this.destroy$.next();
    this.destroy$.complete();

    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }

  private addScrollListener(): void {
    this.scrollHandler = () => {
      const scrollY = window.scrollY;
      
      if (this.shouldRestoreScroll && scrollY < this.savedScrollPosition - 10) {
        window.scrollTo({ top: this.savedScrollPosition, behavior: 'auto' });
      }
    };
    window.addEventListener('scroll', this.scrollHandler, { passive: false });
  }

  onScrollToInscription(): void {
    void this.router.navigate(['/cadastro-parceiros/inscricao/comece-aqui']);
  }

  onConsultarStatus(event?: Event): void {
    if (event) {
      event.preventDefault();
    }

    if (this.statusForm.invalid || this.isConsultingStatus) {
      this.statusForm.markAllAsTouched();
      return;
    }

    this.isConsultingStatus = true;
    const email = this.statusForm.get('email')?.value;
    
    if (!email) {
      this.isConsultingStatus = false;
      return;
    }
    
    this.partnerService
      .sendTokenEmail(email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('Email sent successfully:', response);
          if (response && response.includes('Token sent to email')) {
            this.toastService.showSuccess(this.i18n.instant('pages.partnerRegistration.errors.emailSuccess'), 5000, 'top-center');
            this.isConsultingStatus = false;
            this.statusStep = 'token';
          } else {
            this.toastService.showWarning(this.i18n.instant('pages.partnerRegistration.errors.emailProcessed'), 5000, 'top-center');
            this.isConsultingStatus = false;
            this.statusStep = 'token';
          }
        },
        error: (error) => {
          console.error('Error sending email:', error);
          this.toastService.showError(this.i18n.instant('pages.partnerRegistration.errors.emailSendError'), 5000, 'top-center');
          this.isConsultingStatus = false;
        },
      });
  }

  onVerificarToken(event?: Event): void {
    if (event) {
      event.preventDefault();
    }

    if (this.tokenForm.invalid || this.isVerifyingToken) {
      this.tokenForm.markAllAsTouched();
      return;
    }

    this.isVerifyingToken = true;
    const token = this.tokenForm.get('token')?.value;
    
    if (!token) {
      this.isVerifyingToken = false;
      return;
    }
    
    this.partnerService
      .getPartnerRequestsByToken(token)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (requests) => {
          console.log('Partner requests retrieved:', requests);
          this.partnerRequests = requests;
          this.isVerifyingToken = false;
          
          if (requests && requests.length > 0) {
            this.statusStep = 'lista';
            this.toastService.showSuccess(
              'Token verificado com sucesso!',
              3000,
              'top-center'
            );
          } else {
            this.toastService.showWarning(
              this.i18n.instant('pages.partnerRegistration.status.messages.noRequests'),
              5000,
              'top-center'
            );
          }
        },
        error: (error) => {
          console.error('Error verifying token:', error);
          let errorMessage =  this.i18n.instant('pages.partnerRegistration.errors.invalidToken');
          
          if (error.status === 401 || error.status === 403) {
            errorMessage = this.i18n.instant('pages.partnerRegistration.errors.invalidToken');
          } else if (error.status === 404) {
            errorMessage = this.i18n.instant('pages.partnerRegistration.status.messages.noRequests');
          } else if (error.status >= 500) {
           errorMessage = this.i18n.instant('pages.partnerRegistration.errors.invalidToken');
          }
          
          this.toastService.showError(errorMessage, 5000, 'top-center');
          this.isVerifyingToken = false;
        },
      });
  }

  voltarParaEmail(): void {
    this.statusStep = 'email';
    this.tokenForm.reset();
  }

  voltarParaToken(): void {
    this.statusStep = 'token';
  }

  irParaToken(event: Event): void {
    event.preventDefault();
    this.statusStep = 'token';
  }

  isStatusApproved(status: string): boolean {
    return status?.toUpperCase() === 'APPROVED' || status?.toUpperCase() === 'APROVADO';
  }

  isStatusAnalysis(status: string): boolean {
    const statusUpper = status?.toUpperCase();
    return statusUpper === 'ANALYSIS' || 
           statusUpper === 'ANÁLISE' || 
           statusUpper === 'EM_ANÁLISE' || 
           statusUpper === 'EM-ANÁLISE';
  }

  isStatusReproved(status: string): boolean {
    return status?.toUpperCase() === 'REPROVED' || status?.toUpperCase() === 'REPROVADO';
  }

  getStatusLabel(status: string): string {
    const statusUpper = status?.toUpperCase();
    if (statusUpper === 'APPROVED' || statusUpper === 'APROVADO') {
      return this.i18n.instant('pages.partnerRegistration.status.statusLabels.approved');
    } else if (statusUpper === 'ANALYSIS' || statusUpper === 'ANÁLISE' || statusUpper === 'EM_ANÁLISE' || statusUpper === 'EM-ANÁLISE') {
      return this.i18n.instant('pages.partnerRegistration.status.statusLabels.analysis');
    } else if (statusUpper === 'REPROVED' || statusUpper === 'REPROVADO') {
      return this.i18n.instant('pages.partnerRegistration.status.statusLabels.reproved');
    } else if (statusUpper === 'REJECTED' || statusUpper === 'REJEITADO') {
      return this.i18n.instant('pages.partnerRegistration.status.statusLabels.rejected');
    } else if (statusUpper === 'PENDING' || statusUpper === 'PENDENTE') {
      return this.i18n.instant('pages.partnerRegistration.status.statusLabels.pending');
    }
    return status;
  }

  realizarNovaConsulta(): void {
    this.statusStep = 'email';
    this.statusForm.reset();
    this.tokenForm.reset();
    this.partnerRequests = [];
  }

  onTabChange(tabId: string): void {
    this.activeTabId = tabId;

    if (tabId === 'acompanhar-status') {
      this.statusStep = 'email';
      this.statusForm.reset();
      this.tokenForm.reset();
    }

    const currentUrl = this.router.url.split('?')[0];
    let newUrl = currentUrl;
    
    if (tabId === 'acompanhar-status') {
      newUrl = `${currentUrl}?tab=acompanhar-status`;
    } else {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete('tab');
      const queryString = urlParams.toString();
      newUrl = queryString ? `${currentUrl}?${queryString}` : currentUrl;
    }
    
    window.history.replaceState({}, '', newUrl);
  }

  get statusEmailControl() {
    return this.statusForm.get('email');
  }
  get tokenControl() {
    return this.tokenForm.get('token');
  }
}

